import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { isClickedOutside, isMobileBreakpoint, isTouchDevice, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages a product hotspot dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The product card dialog.
 * @property {HTMLButtonElement} trigger - The hotspot button.
 * @property {HTMLAnchorElement} productLink - The product link.
 *
 * @extends Component<Refs>
 */
export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];

  /** @type {(() => void) | null} */
  #pointerenterHandler = null;

  /** @type {number | null} */
  openTimer = null;

  /** @type {number | null} */
  closeTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#handleBreakpointChange();

    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#removeDesktopListeners();

    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * Open the quick-add modal.
   *
   * @returns {void}
   */
  #openQuickAddModal() {
    const quickAddComponent =
      /** @type {QuickAddComponent | null} */ (
        this.querySelector('quick-add-component')
      );

    if (!quickAddComponent) return;

    quickAddComponent.handleClick(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
      })
    );
  }

  /**
   * Set up desktop hover event listeners.
   *
   * @returns {void}
   */
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    const pointerenterHandler = () => {
      this.#cancelCloseTimer();

      if (dialog.open) return;

      if (this.openTimer !== null) {
        clearTimeout(this.openTimer);
      }

      this.openTimer = window.setTimeout(() => {
        this.openTimer = null;
        this.showDialog();
      }, 120);
    };

    this.#pointerenterHandler = pointerenterHandler;

    trigger.addEventListener('pointerenter', pointerenterHandler);
    trigger.addEventListener('pointerleave', this.#handlePointerLeave);

    dialog.addEventListener('pointerenter', this.#handlePointerEnter);
    dialog.addEventListener('pointerleave', this.#handlePointerLeave);
  }

  /**
   * Remove desktop hover event listeners.
   *
   * @returns {void}
   */
  #removeDesktopListeners() {
    const { trigger, dialog } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener(
        'pointerenter',
        this.#pointerenterHandler
      );

      this.#pointerenterHandler = null;
    }

    trigger.removeEventListener(
      'pointerleave',
      this.#handlePointerLeave
    );

    dialog.removeEventListener(
      'pointerenter',
      this.#handlePointerEnter
    );

    dialog.removeEventListener(
      'pointerleave',
      this.#handlePointerLeave
    );

    if (this.openTimer !== null) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }

    this.#cancelCloseTimer();
  }

  /**
   * Handle responsive breakpoint changes.
   *
   * Desktop uses hover.
   * Mobile uses the click event configured in the template.
   *
   * @returns {void}
   */
  #handleBreakpointChange = () => {
    this.#removeDesktopListeners();

    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  /**
   * Cancel a pending dialog close.
   *
   * @returns {void}
   */
  #cancelCloseTimer() {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  /**
   * Schedule the dialog to close after a short delay.
   *
   * The delay gives the customer enough time to move the cursor
   * from the hotspot button to the product card.
   *
   * @returns {void}
   */
  #scheduleDialogClose() {
    this.#cancelCloseTimer();

    this.closeTimer = window.setTimeout(() => {
      this.closeTimer = null;

      const { trigger, dialog } = this.refs;

      const triggerHovered = trigger.matches(':hover');
      const dialogHovered = dialog.matches(':hover');
      const dialogFocused = dialog.matches(':focus-within');

      if (!triggerHovered && !dialogHovered && !dialogFocused) {
        this.closeDialog();
      }
    }, 250);
  }

  /**
   * Handle the pointer entering the product card.
   *
   * @returns {void}
   */
  #handlePointerEnter = () => {
    this.#cancelCloseTimer();
  };

  /**
   * Handle the pointer leaving the hotspot or product card.
   *
   * @returns {void}
   */
  #handlePointerLeave = () => {
    if (this.openTimer !== null) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }

    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.#scheduleDialogClose();
  };

  /**
   * Calculate the placement of the product dialog.
   *
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;

    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) {
      return;
    }

    const BUTTON_GAP = 10;
    const CONTAINER_GAP = 10;
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    const containerRect = hotspotsContainer.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    /*
     * Temporarily display the dialog invisibly so its dimensions
     * can be measured before deciding where to position it.
     */
    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const {
      width: dialogWidth,
      height: dialogHeight,
    } = dialog.getBoundingClientRect();

    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');

    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    const spaceRight =
      containerRect.width - buttonRight - CONTAINER_GAP;

    const spaceLeft = buttonLeft - CONTAINER_GAP;

    let x = 'right';

    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }

    let y = 'bottom';
    let verticalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY = buttonTop + dialogHeight;

      if (
        dialogEndY >
        containerRect.height - CONTAINER_GAP
      ) {
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';

        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (
          dialogEndY >
          containerRect.height - CONTAINER_GAP
        ) {
          verticalOffset = -(
            dialogEndY -
            (containerRect.height - CONTAINER_GAP)
          );
        }
      } else if (dialogStartY < CONTAINER_GAP) {
        verticalOffset = CONTAINER_GAP - dialogStartY;
        y = 'bottom';
      }
    } else {
      const spaceBelow =
        containerRect.height - buttonBottom;

      if (spaceBelow >= dialogHeight + TOTAL_GAP) {
        y = 'bottom';
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = 'top';
      } else {
        y =
          buttonTop < containerRect.height / 2
            ? 'bottom'
            : 'top';
      }
    }

    dialog.dataset.placement = `${x},${y}`;

    if (verticalOffset !== 0) {
      dialog.style.setProperty(
        '--dialog-vertical-offset',
        `${verticalOffset}px`
      );
    } else {
      dialog.style.removeProperty(
        '--dialog-vertical-offset'
      );
    }

    return new Promise((resolve) => {
      setTimeout(resolve, 100);
    });
  }

  /**
   * Get the product link for the hotspot product.
   *
   * @returns {HTMLAnchorElement | null}
   */
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Handle hotspot click.
   *
   * Touch devices and mobile open the quick-add modal.
   * Desktop opens the product card dialog.
   *
   * @param {MouseEvent} event - The click event.
   * @returns {void}
   */
  handleHotspotClick = (event) => {
    if (isMobileBreakpoint() || isTouchDevice()) {
      event.preventDefault();
      event.stopPropagation();

      this.#openQuickAddModal();
      return;
    }

    this.showDialog();
  };

  /**
   * Show the product card dialog.
   *
   * @returns {Promise<void>}
   */
  showDialog = async () => {
    const { dialog } = this.refs;

    this.#cancelCloseTimer();

    if (dialog.open) return;

    await this.#calculateDialogPlacement();

    dialog.dataset.showing = 'true';
    dialog.show();

    document.body.addEventListener(
      'click',
      this.lightDismissMouse
    );

    document.body.addEventListener(
      'keydown',
      this.lightDismissKeyboard
    );

    document.body.addEventListener(
      'keyup',
      this.lightDismissKeyboard
    );
  };

  /**
   * Close the product card dialog.
   *
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    this.#cancelCloseTimer();

    if (!dialog.open) return;

    dialog.dataset.closing = 'true';
    dialog.close();

    document.body.removeEventListener(
      'click',
      this.lightDismissMouse
    );

    document.body.removeEventListener(
      'keydown',
      this.lightDismissKeyboard
    );

    document.body.removeEventListener(
      'keyup',
      this.lightDismissKeyboard
    );

    const animations = dialog.getAnimations({
      subtree: true,
    });

    await Promise.allSettled(
      animations.map((animation) => animation.finished)
    );

    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;

      dialog.style.removeProperty(
        '--dialog-vertical-offset'
      );
    }
  };

  /**
   * Close the dialog when clicking outside it.
   *
   * @param {MouseEvent} event - The click event.
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Close the dialog when Escape is pressed or focus leaves it.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;

    const escapePressed =
      event.type === 'keydown' &&
      event.key === 'Escape';

    const focusLeftDialog =
      event.type === 'keyup' &&
      !dialog.matches(
        ':is(:focus, :focus-visible, :focus-within)'
      );

    if (escapePressed || focusLeftDialog) {
      this.closeDialog();
    }
  };
}

customElements.define(
  'product-hotspot-component',
  ProductHotspotComponent
);